/**
 * Loading placeholders for the lazily loaded routes (editor, player, generation
 * status, admin, create). They live outside the lazy chunks so App's Suspense
 * fallback can show the page's own skeleton while its code downloads — the page
 * then mounts over the same shape instead of flashing a bare "Loading…" line
 * first. Each mirrors its page's layout with the page's own classes; the
 * shimmer is aria-hidden and a role="status" line is the accessible signal.
 */
import { useT } from "../lib/i18n/store.js";
import type { Page } from "../types.js";

/**
 * Loading placeholder that mirrors the editor's tabbed shell (top bar with its
 * actions and tab strip · AI column · scene list + graph · inspector) with the
 * same classes, so the columns sit where the real ones will and the layout
 * doesn't jump when the novel arrives — and so a slow open shows a skeleton
 * instead of flashing the "Editor unavailable" error.
 */
export function EditorSkeleton() {
  const t = useT();
  return (
    <div className="editor-shell editor-skeleton" aria-busy="true">
      <p className="sr-only" role="status">{t("editor.openingEditor")}</p>
      <div className="editor-topbar" aria-hidden="true">
        <div className="editor-topbar-main">
          <div className="skeleton editor-skeleton-back" />
          <div className="editor-skeleton-title">
            <div className="skeleton skeleton-line skeleton-line--title" />
          </div>
          <div className="editor-skeleton-actions">
            {Array.from({ length: 3 }).map((_, index) => (
              <div key={index} className="skeleton editor-skeleton-btn" />
            ))}
          </div>
        </div>
        <div className="editor-tabs">
          {Array.from({ length: 5 }).map((_, index) => (
            <div key={index} className="editor-tab editor-skeleton-tab">
              <span className="skeleton skeleton-line" />
            </div>
          ))}
        </div>
      </div>
      <div className="editor-body">
        <div className="ai-bar editor-skeleton-ai" aria-hidden="true">
          <div className="skeleton skeleton-line skeleton-line--short" />
          <div className="skeleton editor-skeleton-bubble" />
          <div className="skeleton editor-skeleton-composer" />
        </div>
        <div className="editor-graph-pane" aria-hidden="true">
          <div className="editor-outline">
            <div className="skeleton skeleton-line skeleton-line--short" />
            {Array.from({ length: 6 }).map((_, index) => (
              <div key={index} className="skeleton editor-skeleton-scene" />
            ))}
          </div>
          <div className="editor-graph" />
        </div>
        <div className="editor-inspector editor-skeleton-inspector" aria-hidden="true">
          <div className="skeleton skeleton-line skeleton-line--title" />
          <div className="skeleton skeleton-line" />
          <div className="skeleton skeleton-line" />
          <div className="skeleton skeleton-line skeleton-line--short" />
          <div className="skeleton editor-skeleton-block" />
        </div>
      </div>
    </div>
  );
}

/**
 * Mirrors the player's reading frame: the HUD (with a working Back, so a slow
 * story can still be left) over the fixed-height dialogue box. Its accessible
 * loading line is the player's persistent live region (or the route fallback's).
 */
export function PlayerSkeleton({ onExit }: { onExit?: () => void }) {
  const t = useT();
  return (
    <section className="player player--loading">
      <header className="player-hud">
        {onExit && (
          <button
            type="button"
            className="player-exit"
            title={t("player.exit")}
            onClick={onExit}
          >
            {t("detail.back")}
          </button>
        )}
        <div className="player-meta" aria-hidden="true">
          <span className="skeleton player-skeleton-meta" />
        </div>
      </header>
      <div className="player-panel" aria-busy="true">
        <div className="player-skeleton" aria-hidden="true">
          <div className="player-skeleton-nav">
            <span className="skeleton player-skeleton-navbtn" />
            <span className="skeleton player-skeleton-progress" />
          </div>
          <div className="player-skeleton-reading">
            <div className="skeleton skeleton-line" />
            <div className="skeleton skeleton-line" />
            <div className="skeleton skeleton-line skeleton-line--short" />
          </div>
          <div className="player-skeleton-choices">
            <div className="skeleton player-skeleton-choice" />
            <div className="skeleton player-skeleton-choice" />
          </div>
        </div>
      </div>
    </section>
  );
}

/**
 * The generation page's three columns (timer + progress · graph canvas ·
 * pipeline stages) while a remembered run is re-attached or the page's code
 * loads. Visual only — the caller supplies the heading and the status line.
 */
export function GenerationStatusSkeleton() {
  const t = useT();
  return (
    <div className="genview-3col" aria-hidden="true">
      <aside className="genview-left">
        <p className="editor-col-label">{t("status.eyebrow")}</p>
        <div className="skeleton genview-skel-timer" />
        <div className="skeleton genview-skel-caption" />
        <div className="genview-progress" />
        <div className="skeleton genview-skel-pct" />
      </aside>
      <div className="editor-graph genview-graph" />
      <aside className="genview-right">
        <p className="editor-col-label">{t("status.stepsTitle")}</p>
        <ol className="genview-stages">
          {Array.from({ length: 7 }).map((_, index) => (
            <li key={index} className="genview-stage genview-stage--pending">
              <span className="genview-stage-marker" />
              <span className="skeleton genview-skel-stage" />
            </li>
          ))}
        </ol>
      </aside>
    </div>
  );
}

/** The admin dashboard's frame (title, tab strip, billing tab's first blocks). */
export function AdminPageSkeleton() {
  const t = useT();
  return (
    <section className="admin-page" aria-busy="true">
      <h1 className="admin-h1">{t("admin.title")}</h1>
      <p className="sr-only" role="status">{t("admin.loading")}</p>
      <div className="admin-tabs" aria-hidden="true">
        {Array.from({ length: 5 }).map((_, index) => (
          <span key={index} className="admin-tab admin-tab--skeleton">
            <span className="skeleton admin-tab-skeleton-bar" />
          </span>
        ))}
      </div>
      <div className="admin-budget admin-budget--skeleton" aria-hidden="true">
        <span className="skeleton admin-budget-skel" />
      </div>
      <h2 className="admin-h2">{t("admin.providersHeading")}</h2>
      <p className="admin-providers-hint">{t("admin.providersHint")}</p>
      <div className="admin-providers" aria-hidden="true">
        {[0, 1].map((index) => (
          <div key={index} className="admin-provider-card">
            <div className="admin-provider-head">
              <span className="skeleton admin-provider-name-skeleton" />
            </div>
            <div className="admin-provider-balance">
              <span className="skeleton admin-provider-balance-skeleton" />
            </div>
            <div className="admin-provider-updated" />
            <div className="skeleton admin-provider-form-skeleton" />
          </div>
        ))}
      </div>
    </section>
  );
}

/** The Create page: its fixed heading copy for real, the idea box as a placeholder. */
function CreatePageSkeleton() {
  const t = useT();
  return (
    <section className="create-page" aria-busy="true">
      <p className="sr-only" role="status">{t("detail.loading")}</p>
      <div className="create-headline">
        <p className="eyebrow">{t("create.eyebrow")}</p>
        <h1 className="create-h1">
          {t("create.h1Line1")}
          <br />
          {t("create.h1Line2")}
        </h1>
        <p className="create-subtitle">{t("create.subtitle")}</p>
      </div>
      <div className="create-form" aria-hidden="true">
        <div className="skeleton create-skeleton-prompt" />
        <div className="skeleton create-skeleton-options" />
      </div>
    </section>
  );
}

/**
 * App's Suspense fallback: the skeleton of the page whose code is downloading.
 * `statusHasJob` says whether the generation page will show a run (its three
 * columns) or only the "no generation started" note.
 */
export function RouteFallback({
  page,
  statusHasJob = true,
  onExitPlayer
}: {
  page: Page;
  statusHasJob?: boolean;
  onExitPlayer?: () => void;
}) {
  const t = useT();
  if (page === "editor") return <EditorSkeleton />;
  if (page === "player") {
    return (
      <>
        <p className="sr-only" role="status">{t("player.loadingStory")}</p>
        <PlayerSkeleton onExit={onExitPlayer} />
      </>
    );
  }
  if ((page === "status" && statusHasJob) || page === "genloop") {
    return (
      <>
        <h1 className="sr-only">{t("status.title")}</h1>
        <p className="sr-only" role="status">{t("detail.loading")}</p>
        <GenerationStatusSkeleton />
      </>
    );
  }
  if (page === "admin") return <AdminPageSkeleton />;
  if (page === "create") return <CreatePageSkeleton />;
  return (
    <section className="page-section" aria-busy="true">
      <p className="sr-only" role="status">{t("detail.loading")}</p>
      <div className="section-heading" aria-hidden="true">
        <div className="skeleton skeleton-line skeleton-line--short" />
        <div className="skeleton skeleton-line skeleton-line--title" />
      </div>
      <div className="skeleton route-skeleton-block" aria-hidden="true" />
    </section>
  );
}
